/**
 * Factory function to create data loaders with standard load/cache/fetch patterns.
 * Reduces boilerplate across CardDataLoader, EquipmentDataLoader, etc.
 *
 * @param {Object} config
 * @param {string} config.jsonPath - Path to the JSON file (e.g., '/data/cards.json')
 * @param {string} config.dataKey - Key in the JSON containing the array (e.g., 'cards')
 * @param {string} config.name - Human-readable name for error messages
 */
export function createDataLoader({ jsonPath, dataKey, name }) {
  return {
    data: null,

    async load() {
      if (this.data) return this.data;

      try {
        const response = await fetch(jsonPath);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        this.data = await response.json();
        return this.data;
      } catch (error) {
        console.error(`Failed to load ${name}:`, error);
        return null;
      }
    },

    async getById(id) {
      const data = await this.load();
      if (!data || !data[dataKey]) return null;

      return data[dataKey].find(item => item.id === id);
    },

    async getAll() {
      const data = await this.load();
      return data?.[dataKey] || [];
    },

    isLoaded() {
      return this.data !== null;
    },

    getByIdSync(id) {
      if (!this.data || !this.data[dataKey]) return null;
      return this.data[dataKey].find(item => item.id === id);
    },

    getAllSync() {
      return this.data?.[dataKey] || [];
    }
  };
}
